import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiUsers, FiCheck, FiX, FiClock, FiDollarSign } from 'react-icons/fi';
import { LoadingIndicator } from '../Tools/LoadingIndicator';
import './Modal.css';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const AffiliateReferralsModal = ({ closeModal, affiliate, idToken }) => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (affiliate && affiliate.id && idToken) {
      fetchAffiliateReferrals();
    }
  }, [affiliate, idToken]);

  // Early return if affiliate is not provided
  if (!affiliate) {
    return (
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
        <div className="p-6 text-center">
          <p className="text-gray-600">No affiliate data available</p>
          <button
            onClick={closeModal}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const fetchAffiliateReferrals = async () => {
    if (!affiliate || !affiliate.id) {
      setError('Invalid affiliate data');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${BACKEND_API_URL}/api/admin/affiliate/${affiliate.id}/referrals`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      setReferrals(response.data || []);
    } catch (err) {
      console.error('Error fetching affiliate referrals:', err);
      setError('Failed to load referrals for this affiliate');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (referral) => {
    if (referral.paymentStatus === 'paid') {
      return (
        <span className="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
          <FiCheck className="mr-1" />
          Paid
        </span>
      );
    } else if (referral.paymentStatus === 'pending') {
      return (
        <span className="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
          <FiClock className="mr-1" />
          Pending
        </span>
      );
    } else if (referral.active && referral.subscriptionDays >= 30 && referral.planName?.toLowerCase() !== 'free') {
      return (
        <span className="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
          <FiDollarSign className="mr-1" />
          Eligible
        </span>
      );
    } else if (referral.active && referral.planName?.toLowerCase() !== 'free') {
      return (
        <span className="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
          <FiClock className="mr-1" />
          In Progress ({30 - referral.subscriptionDays} days left)
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
          <FiX className="mr-1" />
          Ineligible
        </span>
      );
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <FiUsers className="mr-2 text-blue-600" />
              Referrals for {affiliate.username || 'Unknown'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {affiliate.email || 'No email'} • {affiliate.totalReferrals || 0} total referrals
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 overflow-y-auto max-h-[60vh]">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingIndicator />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-600 mb-2">Error</div>
            <p className="text-gray-600">{error}</p>
          </div>
        ) : referrals.length === 0 ? (
          <div className="text-center py-12">
            <FiUsers className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No referrals found for this affiliate</p>
          </div>
        ) : (
          <div className="space-y-4">
            {referrals.map((referral, index) => (
              <div
                key={referral.id || index}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {referral.referredUserName || referral.referredUserEmail?.split('@')[0] || 'User'}
                        </h4>
                        <p className="text-sm text-gray-500">{referral.referredUserEmail}</p>
                      </div>
                      {getStatusBadge(referral)}
                    </div>
                    
                    <div className={`grid gap-4 mt-3 ${
                      (referral.signupCommissionEarned && referral.signupCommissionEarned > 0) 
                        ? 'grid-cols-2 md:grid-cols-5' 
                        : 'grid-cols-2 md:grid-cols-4'
                    }`}>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Referred Date</p>
                        <p className="text-sm text-gray-900">{formatDate(referral.referredAt)}</p>
                      </div>
                      
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Plan</p>
                        <p className="text-sm text-gray-900">
                          {referral.planName === 'free' ? (
                            <span className="text-gray-500 capitalize">Free</span>
                          ) : (
                            <span className="text-blue-600 capitalize font-medium">{referral.planName}</span>
                          )}
                          {referral.billingCycle !== '-' && (
                            <span className="text-xs text-gray-500 ml-1">({referral.billingCycle})</span>
                          )}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Subscription Days</p>
                        <p className="text-sm text-gray-900">{referral.subscriptionDays || 0}</p>
                      </div>
                      
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Subscription Commission</p>
                        <p className="text-sm font-medium text-green-600">
                          ${(referral.commissionEarned || 0).toFixed(2)}
                        </p>
                      </div>
                      
                      {/* Signup Commission - Only show if there's a signup commission earned */}
                      {(referral.signupCommissionEarned && referral.signupCommissionEarned > 0) && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Signup Commission</p>
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-blue-600">
                              ${referral.signupCommissionEarned.toFixed(2)}
                            </p>
                            {referral.signupCommissionPaid ? (
                              <span className="ml-2 px-1 py-0.5 inline-flex items-center text-xs leading-4 font-semibold rounded bg-green-100 text-green-800">
                                <FiCheck className="mr-1" />
                                Paid
                              </span>
                            ) : (
                              <span className="ml-2 px-1 py-0.5 inline-flex items-center text-xs leading-4 font-semibold rounded bg-yellow-100 text-yellow-800">
                                <FiClock className="mr-1" />
                                Unpaid
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-3 flex items-center">
                      <span className={`px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${
                        referral.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {referral.active ? (
                          <>
                            <FiCheck className="mr-1" />
                            Active Subscription
                          </>
                        ) : (
                          <>
                            <FiX className="mr-1" />
                            Inactive
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Showing {referrals.length} referrals
          </div>
          <button
            onClick={closeModal}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AffiliateReferralsModal; 